import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { StyledEngineProvider, Theme, ThemeProvider } from '@mui/material/styles';
import { SnackbarsProvider } from 'components/_shared/snackbars';
import { AuthProvider } from 'components/auth';
import { WorkspaceProvider } from 'components/workspace/context';
import { LanguageProvider } from 'components/language';
import { LanguageContextProvider } from 'components/language/context/LanguageContext';
import { translationMessages } from 'configs/formatTranslationMessages';
import theme from 'configs/theme';
import App from 'containers/App';
import React from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from 'react-query';
import 'styles/scss/style.scss';
import '@npm-questionpro/wick-ui-lib/dist/style.css';
import '@npm-questionpro/wick-ui-icon/dist/wu-icon.css';
import 'styles/icon-fix.css';

const queryClient = new QueryClient();

declare module '@mui/styles/defaultTheme' {
  // eslint-disable-next-line @typescript-eslint/no-empty-interface
  interface DefaultTheme extends Theme {}
}

/* Auto-reload when a stale chunk fails to load (e.g. after a new deploy) */
window.addEventListener('error', (e) => {
  if (e.message?.includes('ChunkLoadError') || e.message?.includes('Loading chunk')) {
    const reloaded = sessionStorage.getItem('chunk_reload');
    if (!reloaded) {
      sessionStorage.setItem('chunk_reload', '1');
      window.location.reload();
    }
  }
});


// Suppress harmless ResizeObserver loop errors
window.addEventListener('error', (e) => {
  if (e.message?.includes('ResizeObserver loop')) { e.stopPropagation(); e.preventDefault(); }
});

const MOUNT_NODE = document.getElementById('root') as HTMLElement;

const root = createRoot(MOUNT_NODE);

const render = (messages: any, Component = App) => {
  root.render(
    <LanguageContextProvider>
      <LanguageProvider messages={messages}>
        <QueryClientProvider client={queryClient}>
          <StyledEngineProvider injectFirst>
            <ThemeProvider theme={theme}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <AuthProvider>
                  <WorkspaceProvider>
                    <SnackbarsProvider>
                      <Component />
                    </SnackbarsProvider>
                  </WorkspaceProvider>
                </AuthProvider>
              </LocalizationProvider>
            </ThemeProvider>
          </StyledEngineProvider>
        </QueryClientProvider>
      </LanguageProvider>
    </LanguageContextProvider>,
  );
};

if (!(window as any).Intl) {
  new Promise(resolve => {
    // eslint-disable-next-line global-require
    resolve(import('intl'));
  })
    .then(() => render(translationMessages))
    .catch(err => {
      throw err;
    });
} else {
  render(translationMessages);
}
